import { HttpClient, HttpHeaders } from '@angular/common/http';
import { ErrorHandler, inject, Injectable } from '@angular/core';
import { catchError, concatMap, from, Observable, of, throwError, toArray } from 'rxjs';
import { API_URL } from '../../utils';
import type { Error } from '../../interfaces/error/error';

@Injectable({
  providedIn: 'root',
})
export class GlobalErrorHandlerService implements ErrorHandler {
  private http = inject(HttpClient);

  private errorCache = new Map<string, number>();
  private apiUrl = inject(API_URL);
  private readonly THROTTLE_TIME = 5000;
  private readonly CACHE_KEY = 'offlineErrors';
  private readonly MAX_CACHED_ERRORS = 20;

  readonly httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
      'Cache-Control': 'no-cache, no-store, must-revalidate',
      Pragma: 'no-cache',
      Expires: '0',
    }),
  };


  handleError(error: any): void {
    const errorKey = this.getErrorKey(error);
    const now = Date.now();

    if (
      this.errorCache.has(errorKey) &&
      now - this.errorCache.get(errorKey)! < this.THROTTLE_TIME
    ) {
      return;
    }

    this.errorCache.set(errorKey, now);
    const err: Error = {
      message: error.message ?? error | error.error,
      stack: error.stack ?? JSON.stringify(error.error),
      timestamp: error.error?.timestamp ?? new Date().toISOString(),
      url: error.url || ' ',
    };

    this.sendDiagnostic(err).subscribe({
      next: () => {},
      error: () => {
        this.cacheErrorLocally(err);
      }
    });
  }

  private cacheErrorLocally(error: Error) {
    const current: Error[] = JSON.parse(localStorage.getItem(this.CACHE_KEY) || '[]');
    current.push(error);

    if (current.length > this.MAX_CACHED_ERRORS) {
      current.shift(); // keep only latest errors
    }

    localStorage.setItem(this.CACHE_KEY, JSON.stringify(current));
  }

  private getErrorKey(error: any): string {
    return `${error.status}-${error.message}-${error.url}`;
  }

  // sendDiagnostic(error: Error): Observable<Error> {
  //   return this.http.post<Error>(
  //     `${this.apiUrl}error`,
  //     error
  //     // this.httpOptions
  //   );
  // }

  sendDiagnostic(error: Error): Observable<Error> {
    return this.http.post<Error>(
      `${this.apiUrl}errors`,
      error,
      this.httpOptions
    ).pipe(
      catchError(err => {
        return throwError(() => err);
      })
    );
  }

  flushLocalErrors(): void {
    const stored = localStorage.getItem(this.CACHE_KEY);
    if (!stored) return;

    const errors: Error[] = JSON.parse(stored);
    if (!errors.length) {
      return;
    }

    const remainingErrors: Error[] = [];
    let sentCount = 0;
    let failedCount = 0;

    const delay = 1000;

    const sendNext = (i: number) => {
      if (i >= errors.length) {
        if (remainingErrors.length) {
          localStorage.setItem(this.CACHE_KEY, JSON.stringify(remainingErrors));
        } else {
          localStorage.removeItem(this.CACHE_KEY);
        }
        return;
      }

      this.sendDiagnostic(errors[i]).pipe(
        catchError(() => {
          failedCount++;
          remainingErrors.push(errors[i]);
          return of(null);
        })
      ).subscribe({
        next: () => sentCount++,
        complete: () => setTimeout(() => sendNext(i + 1), delay),
      });
    };

    sendNext(0);
  }

}
